import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faPen, faSave } from '@fortawesome/sharp-solid-svg-icons';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { isMobile } from 'react-device-detect';

import { ITabProps } from '@/components/tabs';
import { AcresFeatureFlags } from '@/constants/constants';
import {
  CUSTOMIZE_LAND_PLAT_MAP_URL,
  PLAT_MAP_URL,
  SAVED_SELECTIONS_URL,
} from '@/constants/urls';
import { AerialMapToast } from '@/features/navigation/topNav/aerialMapToast';
import {
  aerialMapboxBackgroundLayer,
  aerialMapboxBackgroundLayerSourceId,
  aerialMapboxLayer,
  aerialMapboxLayerSourceId,
} from '@/features/navigation/topNav/layers/aerialMapLayers';
import { useGetFeatureFlag } from '@/hooks/useGetFeatureFlag';
import { useAuth } from '@/providers/auth-provider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { useToastContext } from '@/providers/ToastProvider';
import { unimplementedFunction } from '@/utils/unimplementedFunction';

type Props = {
  children: ReactNode;
};

export enum TabTypes {
  MAP = 'map',
  CUSTOMIZE = 'customize',
  PORTFOLIO = 'portfolio',
}

type NavigationContextType = {
  expandMobilePanel: boolean;
  setExpandMobilePanel: Dispatch<SetStateAction<boolean>>;
  currentMobileTab?: string;
  setCurrentMobileTab: Dispatch<SetStateAction<string | undefined>>;
  getTabMap: (isMobile?: boolean) => Partial<Record<TabTypes, ITabProps>>;
  aerialMapIsOpen: boolean;
  setAerialMapIsOpen: Dispatch<SetStateAction<boolean>>;
  closeAerialMap: () => void;
  openAerialMap: () => void;
  downloadAppCTAIsOpen: boolean;
  setDownloadAppCTAIsOpen: Dispatch<SetStateAction<boolean>>;
};

const defaultNavigationContext: NavigationContextType = {
  aerialMapIsOpen: false,
  closeAerialMap: unimplementedFunction,
  currentMobileTab: undefined,
  downloadAppCTAIsOpen: false,
  expandMobilePanel: false,
  getTabMap: () => ({
    [TabTypes.MAP]: {
      label: 'Map',
      value: TabTypes.MAP,
    },
    [TabTypes.CUSTOMIZE]: {
      label: 'Customize',
      value: TabTypes.CUSTOMIZE,
    },
    [TabTypes.PORTFOLIO]: {
      label: 'Portfolio',
      value: TabTypes.PORTFOLIO,
    },
  }),
  openAerialMap: unimplementedFunction,
  setAerialMapIsOpen: unimplementedFunction,
  setCurrentMobileTab: unimplementedFunction,
  setDownloadAppCTAIsOpen: unimplementedFunction,
  setExpandMobilePanel: unimplementedFunction,
};

const NavigationContext = createContext(defaultNavigationContext);

export const tabUrls: Record<TabTypes, string> = {
  [TabTypes.MAP]: PLAT_MAP_URL,
  [TabTypes.CUSTOMIZE]: CUSTOMIZE_LAND_PLAT_MAP_URL,
  [TabTypes.PORTFOLIO]: SAVED_SELECTIONS_URL,
};

export const NavigationProvider: FC<Props> = ({ children }) => {
  const [expandMobilePanel, setExpandMobilePanel] = useState(true);
  const [currentMobileTab, setCurrentMobileTab] = useState<string | undefined>(
    undefined
  );
  const [aerialMapIsOpen, setAerialMapIsOpen] = useState(false);
  const [downloadAppCTAIsOpen, setDownloadAppCTAIsOpen] = useState(isMobile);
  const { userCanAccessCustomize, userCanAccessPortfolio } = useAuth();
  const { mapInstance: map } = useMapboxContext();
  const { clearToastContent, setToastContent } = useToastContext();
  const isNewSelectionsPanelActive = useGetFeatureFlag(
    AcresFeatureFlags.TEMP_NEW_SELECTIONS_PANEL
  );

  const getTabMap = useCallback(
    (isMobileLocal = false) => {
      const tabMap: Partial<Record<TabTypes, ITabProps>> = {
        [TabTypes.MAP]: {
          disabled: aerialMapIsOpen,
          label: isMobileLocal ? <FontAwesomeIcon icon={faMap} /> : 'Map',
          value: TabTypes.MAP,
        },
        [TabTypes.CUSTOMIZE]: {
          disabled: aerialMapIsOpen,
          label: isMobileLocal ? <FontAwesomeIcon icon={faPen} /> : 'Customize',
          value: TabTypes.CUSTOMIZE,
        },
        [TabTypes.PORTFOLIO]: {
          disabled: aerialMapIsOpen,
          label: isMobileLocal ? (
            <FontAwesomeIcon icon={faSave} />
          ) : (
            'Portfolio'
          ),
          value: TabTypes.PORTFOLIO,
        },
      };

      if (!userCanAccessPortfolio || isMobile) {
        // The Portfolio layout is quite broken on mobile
        delete tabMap[TabTypes.PORTFOLIO];
      }

      if (!userCanAccessCustomize || isNewSelectionsPanelActive) {
        delete tabMap[TabTypes.CUSTOMIZE];
      }

      return tabMap;
    },
    [
      aerialMapIsOpen,
      userCanAccessPortfolio,
      userCanAccessCustomize,
      isNewSelectionsPanelActive,
    ]
  );

  const openAerialMap = useCallback(() => {
    setToastContent(<AerialMapToast />, true);
  }, [setToastContent]);

  const closeAerialMap = useCallback(() => {
    if (map?.getLayer(aerialMapboxLayer.id)) {
      map.removeLayer(aerialMapboxLayer.id);
    }
    if (map?.getSource(aerialMapboxLayerSourceId)) {
      map.removeSource(aerialMapboxLayerSourceId);
    }
    if (map?.getLayer(aerialMapboxBackgroundLayer.id)) {
      map?.removeLayer(aerialMapboxBackgroundLayer.id);
    }

    if (map?.getSource(aerialMapboxBackgroundLayerSourceId)) {
      map?.removeSource(aerialMapboxBackgroundLayerSourceId);
    }

    clearToastContent();
  }, [clearToastContent, map]);

  const value: NavigationContextType = useMemo(
    () => ({
      aerialMapIsOpen,
      closeAerialMap,
      currentMobileTab,
      downloadAppCTAIsOpen,
      expandMobilePanel,
      getTabMap,
      openAerialMap,
      setAerialMapIsOpen,
      setCurrentMobileTab,
      setDownloadAppCTAIsOpen,
      setExpandMobilePanel,
    }),
    [
      currentMobileTab,
      expandMobilePanel,
      getTabMap,
      aerialMapIsOpen,
      closeAerialMap,
      openAerialMap,
      downloadAppCTAIsOpen,
      setDownloadAppCTAIsOpen,
    ]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigationContext = () => useContext(NavigationContext);
